import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiCalendar, FiCheckSquare, FiChevronRight, FiShield } from "react-icons/fi";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  type BuyerBidV2EspdDto,
  useGetParticipationRequestForBuyerQuery,
  useGetSourcingEventQuery,
} from "../../../../../autogen/bff-api";
import { isSchemeProcedure } from "../../../../sourcing-events/buyer/wizard/utils/procedure";
import { isNotFoundError } from "../../../../../common/errors/isNotFoundError";
import { useDisplayDateWithTime } from "../../../../../common/formatting/useDisplayDateWithTime";
import { participationStatusBadge } from "../../../../../common/qualification/participationStatusBadge";
import { urls } from "../../../../../urls";
import { EspdTab } from "../../bid-detail/EspdTab";
import { HeaderStat } from "../../bid-detail/HeaderStat";
import { getRequestQualificationProgress } from "./requestQualification";
import { RequestDecisionTab } from "./RequestDecisionTab";
import { RequestDocumentsTab } from "./RequestDocumentsTab";
import { RequestQualificationsTab } from "./RequestQualificationsTab";

const DASH = "–";

type RequestDetailTab = "qualifications" | "espd" | "decision" | "documents";

// t("Qualification requirements") t("ESPD") t("Qualification decision") t("Documents")
const TAB_KEYS: { key: RequestDetailTab; labelKey: string }[] = [
  { key: "qualifications", labelKey: "Qualification requirements" },
  { key: "espd", labelKey: "ESPD" },
  { key: "decision", labelKey: "Qualification decision" },
  { key: "documents", labelKey: "Documents" },
];

const DEFAULT_TAB: RequestDetailTab = "qualifications";

const parseTab = (value: string | null): RequestDetailTab =>
  TAB_KEYS.find((tab) => tab.key === value)?.key ?? DEFAULT_TAB;

const formatEspdHeader = (
  espd: BuyerBidV2EspdDto | null | undefined,
  t: (value: string, options?: { count: number }) => string,
) => {
  if (!espd?.hasResponse) return { value: DASH, tone: "default" as const, pending: true };
  if (espd.deviationCount > 0) {
    return { value: t("espd_deviation_count", { count: espd.deviationCount }), tone: "issue" as const, pending: false };
  }
  if (espd.unansweredCount > 0) {
    return {
      value: t("espd_unanswered_count", { count: espd.unansweredCount }),
      tone: "neutral" as const,
      pending: false,
    };
  }
  return { value: t("OK"), tone: "ok" as const, pending: false };
};

// Buyer detail page for one forespørsel om å delta — the stage-1 sibling of BidDetailView.
export const RequestDetailView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId, requestId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  if (!eventId || !requestId) {
    return (
      <NotFound
        title={t("This link is not valid.")}
        backUrl={urls.v2.sourcingEvents.viewAll}
        backLabel={t("Back to procurements")}
        onNavigate={navigate}
      />
    );
  }

  return (
    <RequestDetailPage
      eventId={eventId}
      requestId={requestId}
      activeTab={parseTab(searchParams.get("tab"))}
      onTabChange={(tab) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", tab);
        setSearchParams(next, { replace: true });
      }}
      navigate={navigate}
    />
  );
};

type Navigate = ReturnType<typeof useNavigate>;

const RequestDetailPage = ({
  eventId,
  requestId,
  activeTab,
  onTabChange,
  navigate,
}: {
  eventId: string;
  requestId: string;
  activeTab: RequestDetailTab;
  onTabChange: (tab: RequestDetailTab) => void;
  navigate: Navigate;
}) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const { data: detail, isLoading, isError, error } = useGetParticipationRequestForBuyerQuery({
    eventId,
    requestId,
  });
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const publicContext = event?.eventContext.type === "Public" ? event.eventContext : null;
  const fromScheme = isSchemeProcedure(publicContext?.procedureType);
  const listTab = fromScheme ? "dpsSuppliers" : "qualification";
  const qualificationTabUrl = `${urls.v2.sourcingEvents.view.go(eventId)}?tab=${listTab}`;

  if (isLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  if (isError || !detail) {
    const notFound = isError && isNotFoundError(error);
    return (
      <NotFound
        title={notFound ? t("The request could not be found.") : t("Could not load the request.")}
        backUrl={qualificationTabUrl}
        backLabel={t("Back to qualification")}
        onNavigate={navigate}
      />
    );
  }

  const request = detail.request;
  const supplierName = request.organizationName || t("Unknown");
  const badge = participationStatusBadge(request.status);
  const activeIndex = Math.max(0, TAB_KEYS.findIndex((tab) => tab.key === activeTab));
  const espdHeader = formatEspdHeader(detail.espd, t);
  const progress = getRequestQualificationProgress(
    detail.selectionCriteria ?? [],
    request.qualificationResponses ?? [],
  );

  return (
    <Box>
      <Grid templateColumns="minmax(0, 1fr)" gap="6" alignItems="start">
        <GridItem minW="0">
          <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
            <Breadcrumb
              separator={<Icon as={FiChevronRight} color="smMuted" boxSize="3" />}
              fontSize="sm"
              mb="3"
              sx={{ ol: { flexWrap: "wrap", rowGap: 1 } }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to={urls.v2.sourcingEvents.viewAll} color="smPrimary">
                  {t("All procurements")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to={urls.v2.sourcingEvents.view.go(eventId)}
                  color="smPrimary"
                  isTruncated
                  maxW={{ base: "180px", md: "280px" }}
                >
                  {detail.eventTitle || t("Untitled")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink as={RouterLink} to={qualificationTabUrl} color="smPrimary">
                  {fromScheme ? t("Suppliers") : t("Qualification")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink color="smMuted" isTruncated maxW={{ base: "180px", md: "280px" }}>
                  {supplierName}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            <HStack spacing="3" align="center" flexWrap="wrap" mb="1">
              <Heading as="h1" size="lg" color="smText">
                {supplierName}
              </Heading>
              <Badge colorScheme={badge.colorScheme} fontSize="sm" px="2" py="0.5" rounded="md">
                {t(badge.labelKey)}
              </Badge>
            </HStack>
            {request.organizationNumber && (
              <Text fontSize="sm" color="smMuted" fontFamily="mono" mb="5">
                {`${t("Organization number")}: ${request.organizationNumber}`}
              </Text>
            )}

            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing="3">
              <HeaderStat
                icon={FiCalendar}
                label={t("Submitted")}
                value={request.submittedAt ? displayDateTime(request.submittedAt) : DASH}
              />
              <HeaderStat
                icon={FiCheckSquare}
                label={t("Requirements reviewed")}
                value={progress.hasRequirements ? `${progress.reviewed}/${progress.total}` : DASH}
                mono
                onClick={activeTab === "qualifications" ? undefined : () => onTabChange("qualifications")}
              />
              <HeaderStat
                icon={FiShield}
                label={t("ESPD")}
                value={espdHeader.value}
                tone={espdHeader.tone}
                pending={espdHeader.pending}
                onClick={activeTab === "espd" ? undefined : () => onTabChange("espd")}
              />
            </SimpleGrid>
          </Box>
        </GridItem>

        <GridItem minW="0">
          <Tabs
            index={activeIndex}
            onChange={(i) => {
              const next = TAB_KEYS[i].key;
              if (next !== activeTab) onTabChange(next);
            }}
            colorScheme="teal"
            isLazy
          >
            <TabList overflowX="auto" overflowY="hidden">
              {TAB_KEYS.map((tab) => (
                <Tab key={tab.key} whiteSpace="nowrap">
                  {t(tab.labelKey)}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              <TabPanel key="qualifications" px="0">
                <RequestQualificationsTab eventId={eventId} detail={detail} />
              </TabPanel>
              <TabPanel key="espd" px="0">
                <EspdTab espd={detail.espd} />
              </TabPanel>
              <TabPanel key="decision" px="0">
                <RequestDecisionTab eventId={eventId} detail={detail} onShowEspd={() => onTabChange("espd")} />
              </TabPanel>
              <TabPanel key="documents" px="0">
                <RequestDocumentsTab detail={detail} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </GridItem>
      </Grid>
    </Box>
  );
};

const NotFound = ({
  title,
  backUrl,
  backLabel,
  onNavigate,
}: {
  title: string;
  backUrl: string;
  backLabel: string;
  onNavigate: Navigate;
}) => (
  <Center py="20">
    <Flex direction="column" align="center" gap="4">
      <Text color="smMuted">{title}</Text>
      <Button variant="outline" size="sm" leftIcon={<Icon as={FiArrowLeft} />} onClick={() => onNavigate(backUrl)}>
        {backLabel}
      </Button>
    </Flex>
  </Center>
);
